import type { HighlightedLines, HighlightToken } from 'codesearch-backend'
import type { TRange } from '../utils/pattern.ts'
import { createMemo, For, Show } from 'solid-js'
import { cn } from '../utils/cn.ts'

/*
  tokens are tokenized on the server and carry both theme colours, so a line paints
  correctly on first render and stays correct across a theme switch. `null` tokens
  means the file wasn't highlighted at all (too big, or no grammar) - the text is
  still the text
*/
export function plainLine(text: string): HighlightToken[] {
  return [{ c: text }]
}

export function lineTokens(tokens: HighlightedLines | null | undefined, index: number, text: string): HighlightToken[] {
  return tokens?.[index] ?? plainLine(text)
}

interface TPiece {
  text: string
  color?: string
  colorDark?: string
  match: boolean
  /** index of the match this piece belongs to, for "current match" styling */
  matchIndex?: number
}

/*
  syntax tokens and search matches are two independent segmentations of the same
  line, and they cut across each other - a match routinely starts inside one token
  and ends inside another. so the line is cut at every boundary of both, and each
  resulting piece knows its colour and whether it's inside a match
*/
function pieces(tokens: HighlightToken[], ranges: TRange[]): TPiece[] {
  if (ranges.length === 0) {
    return tokens.map(it => ({ text: it.c, color: it.l, colorDark: it.d, match: false }))
  }

  const out: TPiece[] = []
  let offset = 0

  for (const token of tokens) {
    const start = offset
    const end = offset + token.c.length
    offset = end

    let cursor = start
    while (cursor < end) {
      const inside = ranges.findIndex(it => it.start <= cursor && cursor < it.end)
      const next = inside >= 0
        ? Math.min(end, ranges[inside].end)
        : Math.min(end, ranges.find(it => it.start > cursor)?.start ?? end)

      out.push({
        text: token.c.slice(cursor - start, next - start),
        color: token.l,
        colorDark: token.d,
        match: inside >= 0,
        matchIndex: inside >= 0 ? inside : undefined,
      })
      cursor = next
    }
  }

  return out
}

export function CodeLine(props: {
  tokens: HighlightToken[]
  ranges?: TRange[]
  /** index (within this line's ranges) of the match the viewer is standing on */
  currentMatch?: number
}) {
  const parts = createMemo(() => pieces(props.tokens, props.ranges ?? []))

  return (
    <For each={parts()}>
      {piece => (
        <Show
          when={piece.color || piece.colorDark || piece.match}
          fallback={piece.text}
        >
          <span
            class={cn('hl', piece.match && (piece.matchIndex === props.currentMatch ? 'hl-match-current' : 'hl-match'))}
            style={{ '--hl-l': piece.color, '--hl-d': piece.colorDark }}
          >
            {piece.text}
          </span>
        </Show>
      )}
    </For>
  )
}
